import ImagingScheduleService from 'business/services/ImagingScheduleService';
import { useEffect, useState } from 'react';
import {
  CaptureProfile,
  ImagingSchedulesDTO,
  PlateInspectionsToScoreDTO,
  PlatesToImageDTO,
  Robot,
  SaveCaptureProfilesDTO,
} from 'business/models/Models';
import { useImagingSettingsRowNum, useSelectedImagingScheduleID } from 'redux/hooks/imagingSetup';
import CaptureProfileService from 'business/services/CaptureProfileService';
import { cloneDeep } from 'lodash';
import { CaptureProfileType } from './types';
import ImagingSettingsService from 'business/services/ImagingSettingsService';
import { useRMDispatch } from 'redux/hooks';
import { setImagingSettingsDTO, setSelectedImagingSettingsID } from 'redux/reducers/imagingSettingsReducer';
import { getImageManagementTasks } from 'redux/thunks/imageManagementTask';
import { getAllImagerQuota } from 'redux/thunks/imagerQuota';

const imsService = new ImagingScheduleService();
const imagingSettingsService = new ImagingSettingsService();
export const useImagingSchedules = () => {
  const [refetch, setRefetch] = useState<boolean>(false);
  const [imagingSchedulesDto, setImagingSchedulesDto] = useState<ImagingSchedulesDTO[]>();
  const [imagingSchedulesOriginal, setImagingSchedulesOriginal] =
    useState<ImagingSchedulesDTO[]>();

  const refetchImagingSchedules = () => {
    setRefetch(!refetch);
  };

  useEffect(() => {
    const fetchData = async () => {
      imsService.getImagingSchedules().then((data) => {
        setImagingSchedulesDto(data);
        setImagingSchedulesOriginal(data);
      });
    };
    fetchData();
  }, [refetch]);

  return {
    imagingSchedulesDto,
    setImagingSchedulesDto,
    imagingSchedulesOriginal,
    refetchImagingSchedules,
  };
};

export const fetchImageManagementTasks = () => {
  const dispatch = useRMDispatch();
  const [refetch, setRefetch] = useState<boolean>(false);
  const refetchImageTaskTreeNodes = () => {
    setRefetch(!refetch);
  };

  useEffect(() => {
    dispatch(getImageManagementTasks());
  }, [refetch]);

  return {
    refetchImageTaskTreeNodes,
  };
};

export const fetchImagerQuota = () => {
  const dispatch = useRMDispatch();
  const [refetch, setRefetch] = useState<boolean>(false);
  const refetchImagerQuota = () => {
    setRefetch(!refetch);
  };

  useEffect(() => {
    dispatch(getAllImagerQuota());
  }, [refetch]);

  return {
    refetchImagerQuota,
  };
};

export const useCaptureProfiles = () => {
  const [captureProfiles, setCaptureProfiles] = useState<CaptureProfile[]>();
  useEffect(() => {
    const fetchData = async () => {
      imsService.getCaptureProfiles().then((data) => {
        setCaptureProfiles(data);
      });
    };
    fetchData();
  }, []);

  return {
    captureProfiles,
  };
};

export const useSelectedImagingSchedule = () => {
  const id = useSelectedImagingScheduleID();
  return id;
};

export const usePlatesToImage = () => {
  const [refetch, setRefetch] = useState<boolean>(false);
  const [platesToImage, setPlatesToImage] = useState<PlatesToImageDTO[]>();
  const [platesToImageOriginal, setPlatesToImageOriginal] =
    useState<PlatesToImageDTO[]>();
  const refetchPlatesToImage = () => {
    setRefetch(!refetch);
  };
  useEffect(() => {
    const fetchData = async () => {
      imsService.getPlatesToImage().then((data) => {
        setPlatesToImage(data);
        setPlatesToImageOriginal(data);
      });
    };
    fetchData();
  }, [refetch]);
  return {
    platesToImage,
    platesToImageOriginal,
    setPlatesToImage,
    refetchPlatesToImage,
  };
};

export const usePlateInspectionsToScore = () => {
  const [refetch, setRefetch] = useState<boolean>(false);
  const [plateInspectionsToScore, setPlateInspectionsToScore] =
    useState<PlateInspectionsToScoreDTO[]>();
  const [plateInspectionsToScoreOriginal, setPlateInspectionsToScoreOriginal] =
    useState<PlateInspectionsToScoreDTO[]>();
  const refetchPlateInspectionsToScore = () => {
    setRefetch(!refetch);
  };
  useEffect(() => {
    const fetchData = async () => {
      imsService.getPlateInspectionsToScore().then((data) => {
        setPlateInspectionsToScore(data);
        setPlateInspectionsToScoreOriginal(data);
      });
    };
    fetchData();
  }, [refetch]);
  return {
    plateInspectionsToScore,
    plateInspectionsToScoreOriginal,
    setPlateInspectionsToScore,
    refetchPlateInspectionsToScore,
  };
};

export const useImagingSettings = () => {
  const [originalImgSettings, setOriginalImgSettings] = useState<CaptureProfileType>({});
  const [currentImgSettings, setCurrentImgSettings] = useState<CaptureProfileType>({});
  const [refetch, setRefetch] = useState<boolean>(false);
  const selectedRows = useImagingSettingsRowNum();
  const dispatch = useRMDispatch();

  const refetchImagingSettings = () => {
    setRefetch(!refetch);
  };
  useEffect(() => {
    async function fetchData(): Promise<SaveCaptureProfilesDTO> {
      const captureProfileService = new CaptureProfileService();
      return captureProfileService.getImageSettings();
    }

    (async () => {
      const captureProfiles = await fetchData();
      const captureProfilesMap = {};
      captureProfiles.captureProfilesDto.forEach((cp) => {
        captureProfilesMap[cp.id] = cp;
      });
      dispatch(setImagingSettingsDTO(captureProfiles));
      setCurrentImgSettings(captureProfilesMap);
      setOriginalImgSettings(cloneDeep(captureProfilesMap));
      const cpKeys = Object.keys(captureProfilesMap);
      const cpId = cpKeys?.[selectedRows?.[0]];
      if (cpId) {
        dispatch(setSelectedImagingSettingsID([cpId]));
      }
    })();
  }, [refetch]);
  return {
    originalImgSettings,
    currentImgSettings,
    setCurrentImgSettings,
    refetchImagingSettings,
  };
};

export const useRobots = () => {
  const [robots, setRobots] = useState<Robot[]>();

  useEffect(() => {
    const fetchData = async () => {
      imagingSettingsService.getRobots().then((data) => {
        setRobots(data);
      });
    };
    fetchData();
  }, []);

  return robots;
};
